import type { ResolvedOptionBadge } from '../status-badge.types';

/** Colour of a piece of row text; unset = the slot's own colour (headline default, detail muted). */
export const AutomaticCheckToneEnum = <const>{
  Default: 'default',
  Muted: 'muted',
  Negative: 'negative',
};
export type AutomaticCheckTone = (typeof AutomaticCheckToneEnum)[keyof typeof AutomaticCheckToneEnum];

/** One run of text in a headline or detail line, so only the problem part carries a colour. */
export interface AutomaticCheckText {
  text: string;
  tone?: AutomaticCheckTone;
  /** Medium weight, for the part that needs attention. */
  strong?: boolean;
  /** Renders the part as a link, e.g. to the filtered list behind a count. */
  link?: AutomaticCheckLink;
}

/** A router link: `commands` for `routerLink`, `queryParams` as the list filters read them. */
export interface AutomaticCheckLink {
  commands: string[];
  queryParams: Record<string, string>;
}

/** One label/value row on the "Automatische controles" card. */
export interface AutomaticCheckRow {
  label: string;
  /** Status pill shown in the headline slot; wins over `headline` when both are set. */
  badge?: ResolvedOptionBadge;
  headline?: AutomaticCheckText[];
  /** Muted second line: last/next check dates and what was checked. */
  detail?: AutomaticCheckText[];
  /** Renders a skeleton line instead of the headline and detail. */
  loading?: boolean;
  /** Screen-reader text while `loading`. */
  loadingLabel?: string;
}

/**
 * Key paths the field reads, relative to an entry's `entry` object (no `entry.` prefix). Every
 * one has a default (`AUTOMATIC_CHECKS_DEFAULT_PATHS`); a module overrides any subset through the
 * config-only `props.checkPaths`.
 */
export interface AutomaticChecksPaths {
  /** Company: KvK status option (custom-select value). */
  kvkStatus: string;
  kvkLastCheck: string;
  kvkNextCheck: string;
  /** Company: the `kvk` field; its value is `{ kvkNumber }` or, in older data, the bare number. */
  kvkNumber: string;
  /** Asset: the linked company (`{ id }`). */
  assetCompany: string;
  /** Asset: set once the plate was looked up at the RDW; only those assets are checked. */
  assetPlateLookedUp: string;
  rdwLastCheck: string;
  rdwNextCheck: string;
  /** Asset: 'Ja' | 'Nee'. */
  insured: string;
}

/** Aggregate of the RDW checks over a company's looked-up assets. */
export interface RdwCheckSummary {
  total: number;
  /** Assets whose next check date is still ahead, i.e. checked in the current cycle. */
  checked: number;
  uninsured: number;
  /** ISO date strings. */
  lastCheck?: string;
  nextCheck?: string;
}

/** State of the live RDW fetch. */
export type RdwCheckState =
  | { status: 'idle' }
  | { status: 'loading' }
  /** `forbidden`: HTTP 403, the viewer may not read the assets module. `lastCheck`: from an earlier successful fetch. */
  | { status: 'error'; forbidden?: boolean; lastCheck?: string }
  | { status: 'ready'; summary: RdwCheckSummary };

/** What the RDW fetch is keyed on; a different company or module refetches. */
export interface RdwQuery {
  moduleId: string | undefined;
  filter: string;
}
